// SM-764 — the session's fixed broker account, as the client sees it.
//
// The gateway fixes ONE broker account per session when it issues the
// session (SM-693) and hands it to the client at bootstrap, on
// /api/auth/me, together with its balance mode. Everything the client
// does with that account is in this file, and it is deliberately little:
//
//	- it is READ from bootstrap and nowhere else. No request body, query
//	  string, header or WS frame the client composes ever carries it —
//	  the gateway derives scope from the session and strips a
//	  client-supplied one anyway (SM-693). A tampered client therefore
//	  cannot influence which account answers;
//	- it is NEVER PERSISTED. No localStorage, sessionStorage or URL
//	  state: refresh, reconnect and a second tab all re-derive it from
//	  /api/auth/me, which is the only honest source of "which account is
//	  this session";
//	- there is no selector, no switch and no cross-account move. A
//	  different account is a different session.
//
// What the balance mode changes here is PRESENTATION only — which native
// funding controls are drawn. UI hiding is never the boundary: the
// gateway, ledger and crypto-wallet-service refuse native funding for a
// broker-funded account server-side (NATIVE_FUNDING_DISABLED), and a
// deep link that gets past this file meets that refusal.
import type { AuthMe } from "./types";

// ── Vocabulary (mirror of contracts/broker/v1 BalanceMode) ───────────

export const BALANCE_MODE_SYSTEM_BROKER = "SYSTEM_BROKER";
export const BALANCE_MODE_BROKER_FUNDED = "BROKER_FUNDED";
export const BALANCE_MODE_THIRD_PARTY_CREDIT = "THIRD_PARTY_CREDIT";

export type BalanceMode =
  | typeof BALANCE_MODE_SYSTEM_BROKER
  | typeof BALANCE_MODE_BROKER_FUNDED
  | typeof BALANCE_MODE_THIRD_PARTY_CREDIT;

/** The trading books a session may offer (mirror of the CASH/GAME modes). */
export type SessionTradingMode = "CASH" | "GAME";

export interface SessionAccount {
  /** The session's fixed broker account; "" when it carries none. */
  brokerAccountId: string;
  /** Its balance mode as bootstrap spelled it; "" when absent. */
  balanceMode: string;
  /** Deposit, deposit address, withdrawal and transfer may be drawn. */
  nativeFundingAllowed: boolean;
  /** The GREEN conversion may be drawn. */
  conversionAllowed: boolean;
  /** The books the CASH/GAME selector may offer. */
  tradingModes: readonly SessionTradingMode[];
  /**
   * SM-630 (15): the account is funded at a provider (THIRD_PARTY_CREDIT).
   * The mini-app states — provider balance, funding pending, Credits —
   * are drawn only for it.
   */
  externalCredit: boolean;
  /**
   * SM-653 (15): the auto-redeem preference may be offered. False for an
   * external-credit account (forced ON server-side, SM-625) and for a
   * mode this build does not know (fail closed).
   */
  autoRedeemConfigurable: boolean;
  /**
   * SM-646 (12): the broker's lifecycle forbids new exposure right now
   * (a paused broker): placement is refused server-side
   * (ACCOUNT_RESTRICTED); the client says so instead of offering it.
   */
  newExposureRestricted: boolean;
  /**
   * SM-811: a provider launch issued the session (EXTERNAL_LAUNCH). Its
   * user signs in through the provider and has no password or second
   * factor here (SM-640), so Password & 2FA is not offered.
   */
  externalLaunch: boolean;
}

const BOTH_MODES: readonly SessionTradingMode[] = ["CASH", "GAME"];
const CASH_ONLY: readonly SessionTradingMode[] = ["CASH"];

/**
 * What a session's bootstrap means for the funding controls and the
 * mode selector. A pure function of the bootstrap body — the same
 * answer on every tab and after every refresh.
 *
 *	absent             the session carries no account (flag off, or none
 *	                   resolved before enforcement): today's client,
 *	                   byte-for-byte — every control as before.
 *	SYSTEM_BROKER      native credit, funded by the user: unchanged.
 *	BROKER_FUNDED      funded by its broker's allocation; no native
 *	                   deposit / withdrawal / transfer in the shared
 *	                   client, and no conversion until a broker
 *	                   conversion policy exists. CASH only (SM-871):
 *	                   the GAME book is a SYSTEM_BROKER product, and
 *	                   order-service refuses a GAME order here.
 *	THIRD_PARTY_CREDIT funded at the provider; no native funding, no
 *	                   conversion, CASH only.
 *	anything else      a mode this build does not know: fail closed —
 *	                   no funding control, CASH only. The server decides
 *	                   regardless; drawing a control it will refuse is
 *	                   the worse mistake.
 *
 * The bootstrap does not (yet) carry a permitted-modes list, so the
 * CASH-only rule for THIRD_PARTY_CREDIT is the contract's own (B7), and
 * for BROKER_FUNDED the SM-871 rule (GAME is SYSTEM_BROKER only), not a
 * per-broker policy read.
 */
export function sessionAccountOf(me: AuthMe | null | undefined): SessionAccount {
  const brokerAccountId = me?.broker_account_id ?? "";
  const balanceMode = me?.balance_mode ?? "";
  const newExposureRestricted = (me?.restrictions ?? []).includes(RESTRICTION_NO_NEW_EXPOSURE);
  const externalLaunch = me?.session_origin === SESSION_ORIGIN_EXTERNAL_LAUNCH;
  const account = { brokerAccountId, balanceMode, newExposureRestricted, externalLaunch };
  if (!balanceMode || balanceMode === BALANCE_MODE_SYSTEM_BROKER) {
    return {
      ...account,
      nativeFundingAllowed: true,
      conversionAllowed: true,
      tradingModes: BOTH_MODES,
      externalCredit: false,
      autoRedeemConfigurable: true,
    };
  }
  if (balanceMode === BALANCE_MODE_BROKER_FUNDED) {
    return {
      ...account,
      nativeFundingAllowed: false,
      conversionAllowed: false,
      tradingModes: CASH_ONLY,
      externalCredit: false,
      autoRedeemConfigurable: true,
    };
  }
  return {
    ...account,
    nativeFundingAllowed: false,
    conversionAllowed: false,
    tradingModes: CASH_ONLY,
    externalCredit: balanceMode === BALANCE_MODE_THIRD_PARTY_CREDIT,
    autoRedeemConfigurable: false,
  };
}

/** The broker every SYSTEM_BROKER account belongs to (contracts SystemBrokerID). */
export const SYSTEM_BROKER_ID = "SYSTEM_BROKER";

/**
 * SM-978: the signed-in session's own broker, for the broker's GOLD image
 * overlay — the broker the server fixed to this session (/auth/me
 * `broker_id`, SM-693), never one read from anywhere else (SM-764). Null
 * for a signed-out visitor, a SYSTEM_BROKER account and a session that
 * carries no broker (attribution off): those see the platform's Gold.
 */
export function sessionBrokerOf(me: AuthMe | null | undefined): { userId: string; brokerId: string } | null {
  const userId = me?.user_id ?? "";
  const brokerId = me?.broker_id ?? "";
  if (!userId || !brokerId || brokerId === SYSTEM_BROKER_ID) return null;
  return { userId, brokerId };
}

/** SM-646: the restriction a paused broker puts on its external sessions. */
export const RESTRICTION_NO_NEW_EXPOSURE = "NO_NEW_EXPOSURE";

/** SM-645: the origin of a session a provider launch issued. */
export const SESSION_ORIGIN_EXTERNAL_LAUNCH = "EXTERNAL_LAUNCH";

// ── The cache-scope register ─────────────────────────────────────────
//
// Private query keys carry the scope the server answers them for, as a
// key segment (SM-765) — the same arrangement mode/current.ts uses for
// the trading mode: plain key factories read it synchronously, and the
// one writer is the bootstrap fetch (fetchMeOrNull), which sets it
// BEFORE the query resolves, so a component re-rendering on the new
// session builds its keys in the new scope.
//
// The segment is the session's account when it carries one, else its
// user (before any account exists every private read is per user), else
// "anon". Two sessions therefore never share a cache entry, even for a
// render: a later sign-in as another account in the same tab keys every
// private query afresh instead of painting the previous figures.
// In memory only — this is a cache partition, not a stored account.

const ANON_SCOPE = "anon";
let scope = ANON_SCOPE;

/** The scope segment for a bootstrap body (null = signed out). */
export function scopeOf(me: AuthMe | null | undefined): string {
  if (!me?.user_id) return ANON_SCOPE;
  if (me.broker_account_id) return `acct:${me.broker_account_id}`;
  return `user:${me.user_id}`;
}

/** The private cache-key segment for the current session. */
export function accountKey(): string {
  return scope;
}

/** Called by fetchMeOrNull on every bootstrap answer, signed out included. */
export function setSessionScope(me: AuthMe | null | undefined): void {
  scope = scopeOf(me);
  if (me?.session_origin === SESSION_ORIGIN_EXTERNAL_LAUNCH) externalLaunchSeen = true;
  else if (me?.user_id) externalLaunchSeen = false;
}

// SM-654 / SM-630 (15): whether this tab's session was opened by a
// provider launch. Such a user has no password (SM-640), so when the
// session ends the sign-in modal is the wrong answer — the honest one is
// "open Smart Market again from your provider app", plus the host
// lifecycle message SESSION_EXPIRED. In memory only, like the scope: a
// reload forgets it, which falls back to today's signed-out page.
let externalLaunchSeen = false;

/** True once this tab held a provider-launched session that is now gone or still live. */
export function externalLaunchSession(): boolean {
  return externalLaunchSeen;
}

/** Test helper — back to the signed-out default. */
export function resetSessionScope(): void {
  scope = ANON_SCOPE;
  externalLaunchSeen = false;
}
